import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getTimeOff, createTimeOff, updateTimeOff, deleteTimeOff, getTeamMembers, getHolidays, createHoliday, deleteHoliday } from '../api/client'
import { toISODate } from '../utils/weeks'
import Modal from '../components/Modal'
import { formatDate } from '../utils/weeks'
import { useAuth } from '../context/AuthContext'
import PageHeader from '../components/PageHeader'
import { useTranslation } from 'react-i18next'
import { ESTADO_COLORES } from '../utils/ausencias'

const TYPES = ['PTO', 'medical', 'work_trip', 'admin', 'permiso']
const TYPE_LABELS = { PTO: 'PTO', medical: 'Medical Leave', work_trip: 'Work Trip', admin: 'Administrative Leave', permiso: 'Permiso' }
const TYPE_COLORS = {
  PTO: 'bg-emerald-100 text-emerald-700',
  medical: 'bg-red-100 text-red-700',
  work_trip: 'bg-blue-100 text-blue-700',
  admin: 'bg-amber-100 text-amber-700',
  permiso: 'bg-violet-100 text-violet-700',
}

const EMPTY_FORM    = { team_member_id: '', start_date: '', end_date: '', type: 'PTO', half_day: false, notes: '' }
const EMPTY_HOLIDAY = { date: '', name: '' }

export default function TimeOff() {
  const { t } = useTranslation('engineering')
  const { user } = useAuth()
  const userRoles = user?.roles || (user?.role ? [user.role] : [])
  // Holidays: admin and PMs. Leave records: admin only -- everyone else asks
  // for theirs in Personas, and a head of their area approves it.
  const canEdit = userRoles.some(r => r === 'admin' || r === 'pm_po')
  const isAdmin = userRoles.includes('admin')

  const [records, setRecords] = useState([])
  const [members, setMembers] = useState([])
  const [holidays, setHolidays] = useState([])
  const [filterMember, setFilterMember] = useState('')
  const [filterType, setFilterType] = useState('')
  // 'vigentes' = approved + pending (what still stands); '' = every state.
  const [filterEstado, setFilterEstado] = useState('vigentes')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [holidayForm, setHolidayForm] = useState(EMPTY_HOLIDAY)
  const [holidayError, setHolidayError] = useState('')
  const [savingHoliday, setSavingHoliday] = useState(false)
  const [showHolidayForm, setShowHolidayForm] = useState(false)
  const [currentOnly, setCurrentOnly] = useState(false)

  const load = async () => {
    setLoading(true)
    // Anyone still with the company can book time off, PMs included.
    const [recs, mems, hols] = await Promise.all([
      getTimeOff({}), getTeamMembers({ active: true }), getHolidays({})])
    setRecords(recs); setMembers(mems); setHolidays(hols)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  // History is kept now -- the capacity panel subtracts vacations from months
  // that have already closed, and it can only do that if the rows survive. So
  // the list shows everything and offers a filter, rather than the records
  // being deleted the day they expire, which is what used to happen.
  const todayISO = toISODate(new Date())
  const filtered = records.filter(r => {
    if (filterMember && r.team_member_id !== Number(filterMember)) return false
    if (filterType   && r.type !== filterType) return false
    const estado = r.estado || 'aprobada'
    if (filterEstado === 'vigentes' && !['aprobada', 'pendiente'].includes(estado)) return false
    if (filterEstado && filterEstado !== 'vigentes' && estado !== filterEstado) return false
    if (currentOnly  && r.end_date < todayISO) return false
    return true
  })
  const pastCount = records.filter(r => r.end_date < todayISO).length

  const memberName = (id) => members.find(m => m.id === id)?.name || '—'

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, team_member_id: members[0]?.id || '' })
    setError(''); setModal('add')
  }
  const openEdit = (r) => {
    setForm({ team_member_id: r.team_member_id, start_date: r.start_date, end_date: r.end_date, type: r.type, half_day: !!r.half_day, notes: r.notes || '' })
    setError(''); setModal(r)
  }
  const closeModal = () => setModal(null)

  const handleSave = async () => {
    if (!form.team_member_id) { setError(t('timeOff.teamMemberRequired')); return }
    if (!form.start_date || !form.end_date) { setError(t('timeOff.datesRequired')); return }
    if (form.end_date < form.start_date) { setError(t('timeOff.endBeforeStart')); return }
    setSaving(true)
    try {
      const payload = { ...form, team_member_id: Number(form.team_member_id), half_day: !!form.half_day, notes: form.notes || null }
      if (modal === 'add') await createTimeOff(payload)
      else await updateTimeOff(modal.id, { start_date: payload.start_date, end_date: payload.end_date, type: payload.type, half_day: payload.half_day, notes: payload.notes })
      load(); closeModal()
    } catch (e) { setError(e.response?.data?.detail || 'Save failed') }
    setSaving(false)
  }

  const handleDelete = async (r) => {
    if (!confirm(t('timeOff.deletionConfirm'))) return
    await deleteTimeOff(r.id); load()
  }

  const handleAddHoliday = async () => {
    if (!holidayForm.date) { setHolidayError(t('timeOff.dateRequired')); return }
    if (!holidayForm.name.trim()) { setHolidayError(t('timeOff.holidayNameRequired')); return }
    setSavingHoliday(true)
    try {
      await createHoliday({ date: holidayForm.date, name: holidayForm.name.trim() })
      setHolidayForm(EMPTY_HOLIDAY); setHolidayError(''); setShowHolidayForm(false)
      setHolidays(await getHolidays({}))
    } catch (e) { setHolidayError(e.response?.data?.detail || 'Failed to save') }
    setSavingHoliday(false)
  }

  const handleDeleteHoliday = async (h) => {
    if (!confirm(t('timeOff.deleteHolidayConfirm', {name: h.name}))) return
    await deleteHoliday(h.id)
    setHolidays(await getHolidays({}))
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader
          title={t('timeOff.title')}
          subtitle={t('timeOff.subtitle')}
          actions={<>
            <Link to="/personas?mias=1"
              className="bg-white border border-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              {t('timeOff.pedirEnPersonas')}
            </Link>
            {isAdmin && (
              <button onClick={openAdd} disabled={members.length === 0}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
                {t('timeOff.addRecord')}
              </button>
            )}
          </>}
        />
        {!isAdmin && (
          <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 max-w-3xl">
            {t('timeOff.soloAdmin')}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <select value={filterMember} onChange={e => setFilterMember(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200">
          <option value="">{t('timeOff.allMembers')}</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200">
          <option value="">{t('timeOff.allTypes')}</option>
          {TYPES.map(tp => <option key={tp} value={tp}>{TYPE_LABELS[tp]}</option>)}
        </select>
        <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200">
          <option value="vigentes">{t('timeOff.estadoFilter.vigentes')}</option>
          <option value="">{t('timeOff.estadoFilter.todas')}</option>
          {['pendiente', 'aprobada', 'rechazada', 'cancelada'].map(e => (
            <option key={e} value={e}>{t(`timeOff.estados.${e}`)}</option>
          ))}
        </select>
        {pastCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={currentOnly}
                   onChange={e => setCurrentOnly(e.target.checked)} />
            {t('timeOff.currentOnly')}
            <span className="text-xs text-gray-400">
              {t('timeOff.pastCount', { count: pastCount })}
            </span>
          </label>
        )}
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">{t('timeOff.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><p>{t('timeOff.noRecords')}</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.memberHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.typeHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.startHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.endHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.estadoHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.notesHeader')}</th>
                {isAdmin && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const estado = r.estado || 'aprobada'
                // Rejected and withdrawn requests never were time off: dimmed.
                const cerrada = estado === 'rechazada' || estado === 'cancelada'
                return (
                <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50 ${cerrada ? 'opacity-50' : ''} ${i === filtered.length - 1 ? 'border-b-0' : ''}`}>
                  <td className="px-5 py-3 font-medium text-gray-900">{memberName(r.team_member_id)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[r.type] || 'bg-gray-100 text-gray-600'}`}>
                      {r.type === 'PTO' ? t('timeOff.typeLabels.PTO') : r.type === 'medical' ? t('timeOff.typeLabels.medical') : r.type === 'work_trip' ? t('timeOff.typeLabels.workTrip') : r.type === 'admin' ? t('timeOff.typeLabels.admin') : r.type}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(r.start_date)}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(r.end_date)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORES[estado] || 'bg-gray-100 text-gray-600'}`}
                      title={r.decision_note || undefined}>
                      {t(`timeOff.estados.${estado}`, estado)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-400 text-xs max-w-xs truncate">{r.notes || '—'}</td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => openEdit(r)} className="text-gray-400 hover:text-gray-900 text-xs font-medium mr-3">Edit</button>
                      <button onClick={() => handleDelete(r)} className="text-gray-400 hover:text-red-500 text-xs font-medium">Delete</button>
                    </td>
                  )}
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Holidays ── */}
      <div className="mt-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{t('timeOff.holidaysTitle')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{t('timeOff.holidaysSubtitle')}</p>
          </div>
          {/* Holidays (and company events) are managed in one place: Administración > Calendario. */}
          {isAdmin && (
            <Link to="/admin/calendario"
              className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              {t('timeOff.gestionarEnAdmin')}
            </Link>
          )}
        </div>

        {canEdit && showHolidayForm && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-5 py-4 mb-4 flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('timeOff.dateField')}</label>
              <input type="date" className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={holidayForm.date} onChange={e => setHolidayForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="flex-1 min-w-48">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('timeOff.holidayNameField')}</label>
              <input type="text" placeholder="e.g. Labour Day"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={holidayForm.name} onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAddHoliday()} />
            </div>
            <div className="flex gap-2 items-end">
              <button onClick={handleAddHoliday} disabled={savingHoliday}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">
                {savingHoliday ? t('projectSetup.saving') : t('planWeek.save')}
              </button>
              <button onClick={() => { setShowHolidayForm(false); setHolidayError('') }}
                className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                {t('planWeek.sendModal.cancel')}
              </button>
            </div>
            {holidayError && <p className="w-full text-red-500 text-xs mt-1">{holidayError}</p>}
          </div>
        )}

        {holidays.length === 0 ? (
          <div className="text-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-xl">
            <p className="text-sm">{t('timeOff.noHolidays')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.holidayDateHeader')}</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.holidayNameHeader')}</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('timeOff.holidayDayHeader')}</th>

                </tr>
              </thead>
              <tbody>
                {holidays.map((h, i) => {
                  const d = new Date(h.date + 'T00:00:00')
                  const DAY_KEYS = ['timeOff.dayNames.sunday','timeOff.dayNames.monday','timeOff.dayNames.tuesday','timeOff.dayNames.wednesday','timeOff.dayNames.thursday','timeOff.dayNames.friday','timeOff.dayNames.saturday']
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6
                  return (
                    <tr key={h.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i === holidays.length - 1 ? 'border-b-0' : ''}`}>
                      <td className="px-5 py-3 text-gray-900 font-medium">{formatDate(h.date)}</td>
                      <td className="px-5 py-3 text-gray-900">{h.name}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                          {t(DAY_KEYS[d.getDay()])}{isWeekend ? ' ' + t('timeOff.weekend') : ''}
                        </span>
                      </td>

                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isAdmin && modal && (
        <Modal title={modal === 'add' ? t('timeOff.addTimeOffModal') : t('timeOff.editTimeOffModal')} onClose={closeModal}>
          <div className="space-y-4">
            {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            {modal === 'add' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeOff.teamMemberField')}</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.team_member_id} onChange={e => setForm(f => ({ ...f, team_member_id: e.target.value }))}>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeOff.typeField')}</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.type} onChange={e => setForm(f => {
                  const type = e.target.value
                  // half_day only valid for admin; reset when switching away
                  return type === 'admin' ? { ...f, type } : { ...f, type, half_day: false }
                })}>
                {TYPES.map(tp => <option key={tp} value={tp}>{TYPE_LABELS[tp]}</option>)}
              </select>
            </div>
            {form.type === 'admin' && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={form.half_day}
                    onChange={e => setForm(f => {
                      const half_day = e.target.checked
                      // a half day is a single day: lock end date to start date
                      return half_day ? { ...f, half_day, end_date: f.start_date } : { ...f, half_day }
                    })} />
                  {t('timeOff.halfDayField')}
                </label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeOff.startField')}</label>
                <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.start_date} onChange={e => setForm(f => (
                    // keep end date in sync while half day is on
                    f.half_day ? { ...f, start_date: e.target.value, end_date: e.target.value } : { ...f, start_date: e.target.value }
                  ))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeOff.endField')}</label>
                <input type="date" disabled={form.half_day}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                  value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeOff.notesField')}</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('timeOff.optionalPlaceholder')} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">
                {saving ? t('projectSetup.saving') : t('planWeek.save')}
              </button>
              <button onClick={closeModal} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t('planWeek.sendModal.cancel')}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
